import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../features/store';
import { THEME } from '../../features/Theme/slice';
import { useTranslation } from 'react-i18next';
import { Typography } from '@thingsw/pitta-design-system';
import { SemanticDarkColors } from '@thingsw/pitta-modules';

const useStyles = makeStyles((theme: Theme) => ({
  BatteryStatusButton: {
    display: 'flex',
    alignItems: 'center',
    ...(theme.direction === 'rtl' ? { marginRight: 25 } : { marginLeft: 25 }),
  },
  btnContainer: {
    display: 'flex',
    padding: 2,
    gap: 4,
  },
  btn: {
    flex: 1,
    height: 80,
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
    borderRadius: 8,
    padding: 8,
    gap: 4,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: (props: any) =>
      props.color === 'dark' ? SemanticDarkColors.primary['0'] : 'white',
    '&:active': {
      backgroundColor: (props: any) => props.colors.primary['6'],
    },
  },
  fleetaWebBtn: {
    flex: 1,
    height: 80,
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
    borderRadius: 8,
    padding: 8,
    gap: 4,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: (props: any) =>
      props.color === 'dark' ? SemanticDarkColors.primary['0'] : 'white',
    '&:not(.disabled):active': {
      border: (props: any) =>
        props.color === 'light'
          ? `1px solid ${props.secondaryColors.primary['7']}`
          : `1px solid ${props.colors.primary['8W']}`,
      backgroundColor: (props: any) =>
        props.color === 'light' ? '#FAFAFA' : props.colors.primary['0'],
      '& span': {
        color: (props: any) =>
          props.color === 'light'
            ? `${props.secondaryColors.primary['7']} !important`
            : `${props.colors.primary['1']}`,
      },
      '& path': {
        stroke: (props: any) =>
          props.color === 'light'
            ? `${props.secondaryColors.primary['7']}`
            : `${props.colors.primary['1']}`,
        fill: (props: any) =>
          props.color === 'light'
            ? props.isHoverFilled && props.secondaryColors.primary['7']
            : props.isHoverFilled && props.colors.primary['1'],
      },
      '& rect': {
        stroke: (props: any) =>
          props.color === 'light'
            ? `${props.secondaryColors.primary['7']}`
            : props.colors.primary['1'],
      },
    },
    '& path': {
      stroke: (props: any) => `${props.colors.primary['1']}`,
    },
    '& rect': {
      stroke: (props: any) => `${props.colors.primary['1']}`,
    },
    cursor: 'pointer',
    '&:not(.disabled):hover': {
      backgroundColor: (props: any) =>
        props.color === 'light' ? '#FAFAFA' : SemanticDarkColors.primary['0'],
      '& span': {
        color: (props: any) =>
          props.color === 'light'
            ? `${props.secondaryColors.primary['7']} !important`
            : `${props.colors.primary['1']}`,
      },
      '& path': {
        stroke: (props: any) =>
          props.color === 'light'
            ? `${props.secondaryColors.primary['7']}`
            : `${props.colors.primary['1']}`,
        fill: (props: any) =>
          props.color === 'light'
            ? props.isHoverFilled && props.secondaryColors.primary['7']
            : props.isHoverFilled && props.colors.primary['1'],
      },
      '& rect': {
        stroke: (props: any) =>
          props.color === 'light'
            ? `${props.secondaryColors.primary['7']}`
            : props.colors.primary['1'],
      },
    },
    '&.disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  rootTransition: {
    transition: theme.transitions.create(['transform', 'height']),
    userSelect: 'none',
  },
  panelClose: {
    height: 118,
  },
  panelOpen: {
    height: 290,
  },
  flex: {
    display: 'flex',
  },
  between: {
    justifyContent: 'space-between',
  },
}));

export interface RenewalCameraMenuProps {
  title: string | React.ReactNode;
  icon: ReactElement;
  onClick: VoidFunction;
  subIcon?: ReactElement;
  isHoverFilled?: boolean;
  disabled?: boolean;
}

export const RenewalCameraMenu = ({
  title,
  icon,
  subIcon,
  onClick,
  isHoverFilled,
  disabled,
}: RenewalCameraMenuProps) => {
  const { colors, color, secondaryColors } = useSelector(
    (state: RootState) => state[THEME]
  );

  const classes = useStyles({ colors, color, secondaryColors, isHoverFilled });
  const { t } = useTranslation();

  return (
    <div 
      className={clsx(classes.fleetaWebBtn, {
        'disabled': disabled,
      })} 
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
    >
      <div
        className={clsx(classes.flex, classes.between)}
        style={{ position: 'relative' }}
      >
        {icon}
        {subIcon}
      </div>
      {typeof title === 'string' ? (
        <Typography
          category='Default'
          variant='Caption'
          htmlColor={colors.primary['1']}
          style={{
            whiteSpace: 'pre-line',
          }}
        >
          {t(title)}
        </Typography>
      ) : (
        title
      )}
    </div>
  );
};
